import dayjs from 'dayjs'
import {
  type AdministrationStatus,
  type MarChartOutcome,
  MedicationStockReason,
  type MedicationTableRow,
  type StockRecord
} from '../types'

type BuildStockRecordArgs = {
  /** The outcome the clicked cell resolved to (`cell.outcomes[0]`). */
  outcome: MarChartOutcome
  /** The MAR chart row the cell belongs to. */
  row: MedicationTableRow
  /** The clicked column's date, DD-MM-YYYY. */
  dateKey: string
}

const formatName = (
  name?: { first: string; last: string } | string
): string => {
  if (!name) {
    return ''
  }
  return typeof name === 'object' ? `${name.first} ${name.last}` : name
}

/**
 * Resolve the unit to display beside a dose.
 *
 * The MAR chart only carries `doseUnit` on outcomes it has a dose snapshot for;
 * back-signed and corrected administrations arrive without one, which used to
 * leave the drawer rendering a bare "1" with no unit (ROST-2972). The row's own
 * medication always knows its unit, so fall back to that before giving up.
 */
const resolveUnit = ({ outcome, row }: BuildStockRecordArgs): string =>
  outcome.dateStatus?.doseUnit ||
  outcome.administration?.medication?.doseUnit ||
  outcome.administration?.quantityType ||
  row.medication?.quantityType ||
  row.medication?.stockUnit ||
  ''

/**
 * Map a MAR chart outcome onto the flattened `StockRecord` the administration
 * drawer, the administration modal and the back-sign modal all read.
 *
 * The MAR chart endpoint returns a nested envelope (`outcome.administration`,
 * with a `dateStatus` snapshot beside it) while those components expect the
 * flat shape the stock endpoints return. This is the single place that bridges
 * the two — it previously existed as three near-identical copies across
 * EMARResidentScreenV2 and MedicationInformationScreenV2, and the unit fallback
 * was fixed in none of them.
 *
 * Returns null when the outcome carries no administration to map.
 */
export const buildStockRecord = (
  args: BuildStockRecordArgs
): StockRecord | null => {
  const { outcome, row, dateKey } = args
  const administration = outcome.administration

  if (!administration) {
    return null
  }

  const dateStatus = outcome.dateStatus
  const medicationSnapshot = administration.medication
  const detail = administration.administration
  const recordedBy = administration.recordedBy

  const doseQuantity =
    medicationSnapshot?.doseQuantity || dateStatus?.doseQuantity || 0

  const administrationStatus = (detail?.status ||
    dateStatus?.status ||
    administration.administrationStatus) as AdministrationStatus

  const roundHour = row.hour
  const roundTime = roundHour
    ? dayjs(dateKey, 'DD-MM-YYYY')
        .hour(parseInt(roundHour.split(':')[0], 10))
        .minute(parseInt(roundHour.split(':')[1], 10))
        .second(0)
        .millisecond(0)
        .toISOString()
    : undefined

  return {
    id: administration._id,
    date:
      administration.date ||
      administration.actionTime ||
      dateStatus?.date ||
      '',
    actionTime:
      administration.actionTime || administration.date || dateStatus?.date,
    action: MedicationStockReason.MED_ADMIN,
    type: 'administration',
    administrationStatus,
    recordedBy: {
      id: recordedBy?.id || '',
      name: formatName(recordedBy?.name),
      picture: recordedBy?.picture || undefined
    },
    notes: administration.notes || [],
    quantityChange:
      administration.stock?.quantityChange ??
      (doseQuantity ? -doseQuantity : 0),
    unit: resolveUnit(args),
    witnessed: administration.witnessed || false,
    witnesses: administration.witnesses || [],
    expectedStock: row.medication?.currentStock || 0,
    stockCount: row.medication?.currentStock || 0,
    changelog: administration.changelog || [],
    administration: {
      ...administration,
      doseQuantity,
      dose: doseQuantity,
      recordedBy: {
        id: recordedBy?.id || '',
        name:
          typeof recordedBy?.name === 'object'
            ? recordedBy.name
            : { first: '', last: recordedBy?.name || '' },
        picture: recordedBy?.picture || undefined
      },
      administrationStatus
    },
    stockChanged:
      detail?.stockAffected === false ? false : dateStatus?.stockChanged,
    altDose:
      medicationSnapshot?.altDose !== undefined
        ? String(medicationSnapshot.altDose)
        : undefined,
    backsignedAt:
      dateStatus?.backsignedAt || administration.backsignedAt || undefined,
    reasonForLateSignature:
      administration.reasonForLateSignature ||
      dateStatus?.reasonForLateSignature ||
      undefined,
    administeredBy:
      administration.administeredBy || dateStatus?.administeredBy || undefined,
    roundTime
  }
}
